import {
  Button,
  lightTheme,
  SystemProblem,
  TextButton,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { problemActionTarget } from '../helpers/problemActionTarget';
import {
  problemAction,
  ProblemActionKind,
} from '../helpers/systemProblemLabels';
import { RowAction } from './ProblemActionButton.styled';

type Props = {
  problem: SystemProblem;
  /** The restaurant's phone, for the "Call restaurant" types. */
  phone?: string | null;
  /** In the detail's footer: a full button beside Mark as Resolved. */
  wide?: boolean;
  /**
   * Actions the current screen does itself instead of opening a new tab:
   * on Location Details "Open SaaS settings" is that screen's own modal.
   */
  inPlace?: InPlaceActions;
};

export type InPlaceActions = Partial<Record<ProblemActionKind, () => void>>;

/**
 * The one thing to do about a problem: assign the delivery, open the
 * providers or the menu, call the restaurant. A text link on the row, a
 * button in the detail's footer. Renders nothing when the problem lacks what
 * the action needs.
 *
 * Its own click only - the row around it opens the problem's detail.
 */
export const ProblemActionButton = ({
  problem,
  phone,
  wide,
  inPlace,
}: Props) => {
  const openInNewTab = useSafeWindowOpen();
  const action = problemAction(problem);
  const target = action && problemActionTarget(action.kind, problem, phone);
  if (!action || !target) return null;

  const onClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const here = inPlace?.[action.kind];
    if (here) {
      here();
    } else if ('tel' in target) {
      window.location.href = `tel:${target.tel}`;
    } else {
      openInNewTab(target.route, { immediate: true });
    }
  };

  if (!wide) {
    return (
      <RowAction>
        <TextButton color={lightTheme.primary} $underline onClick={onClick}>
          {action.label}
        </TextButton>
      </RowAction>
    );
  }

  return (
    <Button $w="200px" $wSM="100%" onClick={onClick}>
      {action.label}
    </Button>
  );
};
